/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import {
    useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle,
} from 'react';
import {
    Box, Typography, Stack, TextField, Alert,
    Switch, FormControlLabel, CircularProgress,
} from '@mui/material';
import Iconify from '../Iconify';
import DentallyBookingFormPayment from './DentallyBookingFormPayment';
import DentallyBookingSuccess from './DentallyBookingSuccess';
import { useDentallyBooking } from '../../hooks/useDentallyBooking';
import { dispatch, useSelector } from '../../redux/store';
import { getDentallyConfigs } from '../../redux/slices/practiceIntegration';
import { parseDentallyDate } from '../../utils/dentallyUtils';
import useDentallyReasonOptions from '../../hooks/useDentallyReasonOptions';
import { sectionCardStyle, sectionLabelStyle } from '../../styles/DentallyBookingStyle';
import { selectAssignableUsers, assignableUserName } from '../../utils/assignableUsers';
import { describeZoneConflict } from '../../utils/dentallyZones';
import {
    filterPracticeUserSelections,
    selectPracticeUsersForPractice,
} from '../../redux/slices/practiceUser';

const BOOKING_PROGRESS_STEPS = [
    { index: 1, label: 'Adding treatment' },
    { index: 2, label: 'Creating appointment' },
    { index: 3, label: 'Booking in Dentally' },
    { index: 4, label: 'Processing payment' },
];

/**
 * Follow-Up Dentally booking confirmation wizard step.
 *
 * Replaces the standalone DentallyFollowUpBookingDialog step 2.
 * Uses forwardRef + useImperativeHandle (submit = no-op) because
 * the internal "Confirm Booking" button drives the submit.
 *
 * Props: ref, selectedSlot, practiceId, existingLead, existingTreatmentId, onClose
 */

const DentallyFollowUpConfirmWizardStep = forwardRef(({
    selectedSlot,
    practiceId,
    existingLead,
    patientData,
    requirePatientDetails = false,
    existingTreatmentId,
    selectedDentallyTreatment,
    assignees = [],
    onClose,
    onStepStateChange,
    onBookingDiagnosticChange,
}, ref) => {
    const allUsers = useSelector((state) => selectPracticeUsersForPractice(state, practiceId));
    const { reasons: reasonOptions, loading: reasonsLoading, error: reasonsError } = useDentallyReasonOptions(practiceId, { verifiedOnly: true });
    const dentallyConfigs = useSelector((state) => state.practiceIntegration?.dentallyConfigs?.results || []);
    const configExportTrigger = dentallyConfigs.find((c) => c.is_active)?.dentally_export_trigger || 'APPOINTMENT';

    // See utils/assignableUsers: one definition, shared with the rest of the CRM.
    // The old filter kept PRACTITIONER, offering Dentally-synced clinicians as
    // lead assignees who cannot be chosen on any other screen.
    const assignableUsers = selectAssignableUsers(allUsers)
        .map((u) => ({ id: u.id, name: assignableUserName(u) }));

    // Load Dentally config if not already in Redux (e.g. user navigated directly to lead detail
    // without visiting Settings → Integrations — without this, configExportTrigger defaults to
    // 'APPOINTMENT' and isPaymentRequired is always false, so the deferred flow never activates)
    useEffect(() => {
        if (practiceId && dentallyConfigs.length === 0) {
            dispatch(getDentallyConfigs(practiceId));
        }
    }, [practiceId]); // eslint-disable-line react-hooks/exhaustive-deps

    const selectionMatchesPractice = String(selectedDentallyTreatment?.practiceId) === String(practiceId);
    const reason = selectionMatchesPractice ? (selectedDentallyTreatment?.dentally_appointment_reason || '') : '';
    const [reasonTouched, setReasonTouched] = useState(false);
    const [duration, setDuration] = useState(30);

    // The slot defines duration; confirmation does not ask for it again.
    useEffect(() => {
        if (!selectedSlot?.start_time || !selectedSlot?.finish_time) return;
        const slotMs = new Date(selectedSlot.finish_time) - new Date(selectedSlot.start_time);
        const slotMinutes = Math.round(slotMs / 60000);
        if (slotMinutes > 0) setDuration(slotMinutes);
    }, [selectedSlot?.start_time, selectedSlot?.finish_time]);
    const [paymentValues, setPaymentValues] = useState({ paymentOption: 'none' });
    const [exportTriggerOverride, setExportTriggerOverride] = useState(null);
    const [contactEmail, setContactEmail] = useState(existingLead?.lead_contact?.email || '');
    const [contactPhone, setContactPhone] = useState(existingLead?.lead_contact?.phone || '');
    const mountedRef = useRef(true);
    const activeConfirmationRef = useRef(null);
    const confirmationScopeKey = [
        practiceId,
        existingLead?.id,
        selectedSlot?.start_time,
        selectedSlot?.finish_time,
        selectedDentallyTreatment?.practiceTreatmentId,
        reason,
    ].join(':');
    const confirmationScopeRef = useRef(confirmationScopeKey);

    if (confirmationScopeRef.current !== confirmationScopeKey) {
        confirmationScopeRef.current = confirmationScopeKey;
        activeConfirmationRef.current = null;
    }

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            activeConfirmationRef.current = null;
        };
    }, []);

    // Patient fields come from the dedicated Patient Details step (patientData prop)
    // Fall back to the lead's own fields if patientData not yet set
    const resolvedSalutation = patientData?.salutation || existingLead?.salutation || '';
    const resolvedDob = patientData?.dob || existingLead?.dob || '';
    const patientDetailsReady = !requirePatientDetails
        || patientData?.patientLinkOutcome === 'unique'
        || Boolean(resolvedSalutation && resolvedDob);

    const exportTrigger = exportTriggerOverride || configExportTrigger;
    const isPaymentRequired = exportTrigger === 'PAYMENT';

    const {
        loading: dentallyLoading,
        result: dentallyResult,
        error: dentallyError,
        bookingStep,
        bookingDiagnostic,
        submit: dentallySubmit,
    } = useDentallyBooking({
        practiceId,
        existingLead,
        existingTreatmentId,
        isPaymentRequired,
        patientLinkOutcome: patientData?.patientLinkOutcome,
    });

    useEffect(() => {
        onBookingDiagnosticChange?.(bookingDiagnostic);
    }, [bookingDiagnostic, onBookingDiagnosticChange]);

    useEffect(() => () => {
        onBookingDiagnosticChange?.(null);
    }, [onBookingDiagnosticChange]);

    const linkChannel = paymentValues.paymentLinkChannel || 'email';
    const needsEmail = paymentValues.paymentOption === 'link' && (linkChannel === 'email' || linkChannel === 'both');
    const needsPhone = paymentValues.paymentOption === 'link' && (linkChannel === 'whatsapp' || linkChannel === 'both');
    const normalizedReason = reason.trim();
    const hasValidReason = reasonOptions.includes(normalizedReason);
    const showReasonError = reasonTouched && !hasValidReason;
    const canConfirm =
        selectedSlot &&
        !selectedSlot._manual &&
        patientDetailsReady &&
        hasValidReason &&
        !dentallyLoading &&
        !dentallyResult?.success &&
        (!isPaymentRequired || paymentValues.paymentOption !== 'none') &&
        (!needsEmail || contactEmail.trim()) &&
        (!needsPhone || contactPhone.trim());

    // The zone covering the chosen slot, stamped on at selection time by the
    // availability results (the only place that already knows). Absent for a
    // manually entered time, and absent when the slot is unzoned.
    const zoneWarning = selectedSlot?._zone
        ? describeZoneConflict([selectedSlot._zone], selectedSlot)
        : null;

    // WHY the button is disabled, in the operator's words. Nine conditions gate
    // `canConfirm` and the UI used to state none of them: the only message,
    // showReasonError, is hidden until the reason field has been touched, so an
    // auto-filled invalid reason produced a grey button and silence. Reported
    // from a live demo as "it was just showing grey, I'm not sure what the
    // problem was". Ordered so the first thing the operator can act on wins.
    const blockingReason = (() => {
        if (dentallyLoading || dentallyResult?.success) return null;
        if (!selectedSlot) return 'Choose an appointment slot to continue.';
        if (selectedSlot._manual) return 'This time was entered manually, so it cannot be booked into Dentally. Pick a slot from the diary.';
        if (!patientDetailsReady) return 'Some patient details Dentally requires are still missing. Check the step before this one.';
        if (!hasValidReason) {
            return reasonOptions.length === 0
                ? 'No appointment reasons are set up for this practice in Dentally yet, so this booking cannot be sent.'
                : 'Choose an appointment reason Dentally recognises.';
        }
        if (isPaymentRequired && paymentValues.paymentOption === 'none') return 'This treatment needs a payment option before it can be booked.';
        if (needsEmail && !contactEmail.trim()) return 'Add an email address to send the payment link to.';
        if (needsPhone && !contactPhone.trim()) return 'Add a mobile number to send the payment link to.';
        return null;
    })();

    // Report step state to parent so the footer can show the right button
    const CONFIRM_MAP = {
        received: { label: 'Confirm & Record Payment', icon: 'mdi:cash-check' },
        link: { label: 'Confirm & Send Link', icon: 'mdi:send-check' },
    };
    const { label: confirmLabel, icon: confirmIcon } = CONFIRM_MAP[paymentValues.paymentOption] || { label: 'Confirm Booking', icon: 'mdi:check-bold' };

    useEffect(() => {
        onStepStateChange?.({
            buttonLabel: confirmLabel,
            buttonIcon: confirmIcon,
            buttonDisabled: !canConfirm,
            buttonLoading: dentallyLoading,
            hideFooterOnSuccess: dentallyResult?.success || false,
        });
    }, [canConfirm, confirmLabel, confirmIcon, dentallyLoading, dentallyResult?.success]);

    const [confirmError, setConfirmError] = useState(null);

    const handleConfirm = useCallback(async () => {
        if (!hasValidReason) {
            setReasonTouched(true);
            return;
        }
        if (
            !canConfirm
            || !mountedRef.current
            || confirmationScopeRef.current !== confirmationScopeKey
            || activeConfirmationRef.current !== null
        ) return;
        const confirmation = {};
        activeConfirmationRef.current = confirmation;
        const ownsConfirmation = () => (
            mountedRef.current
            && confirmationScopeRef.current === confirmationScopeKey
            && activeConfirmationRef.current === confirmation
        );
        setConfirmError(null);

        try {
            // Contact details (email/phone) are no longer PATCHed to the lead here —
            // they're passed straight through to dentallySubmit → the payment-link
            // command (contract A), which persists them to lead_contact server-side
            // as part of the send. Two round-trips racing on the same field was the
            // failure mode this used to guard against with its own error message.
            const startIso = selectedSlot.start_time;
            const finishIso = selectedSlot.finish_time;
            if (!startIso || !finishIso) {
                setConfirmError('Selected slot is missing date/time information. Please go back and select a slot.');
                return;
            }
            if (!ownsConfirmation()) return;
            await dentallySubmit({
                practitionerId: selectedSlot._queried_practitioner_id || selectedSlot.practitioner_id,
                reason: normalizedReason,
                treatmentName: selectedDentallyTreatment?.name,
                dateTime: new Date(startIso),
                durationMinutes: duration,
                method: 'IN_PRACTICE',
                assignees: filterPracticeUserSelections(assignableUsers, assignees).map((u) => u.id),
                email: contactEmail,
                phone: contactPhone,
                paymentOption: paymentValues.paymentOption || 'none',
                paymentAmount: paymentValues.paymentAmount,
                paymentType: paymentValues.paymentType || 'DEPOSIT',
                receivedMode: paymentValues.receivedMode,
                paymentLinkChannel: paymentValues.paymentLinkChannel || 'email',
                // Patient fields collected in the Patient Details step
                ...(resolvedSalutation ? { salutation: resolvedSalutation } : {}),
                ...(resolvedDob ? { dob: resolvedDob } : {}),
                persistedPatientFields: patientData?.persistedPatientFields || {
                    salutation: false,
                    dob: false,
                },
            });
        } catch {
            if (!ownsConfirmation()) return;
            console.error('[DentallyFollowUpConfirm] Unexpected booking error');
            setConfirmError('Could not complete the booking. Please try again.');
        } finally {
            if (activeConfirmationRef.current === confirmation) {
                activeConfirmationRef.current = null;
            }
        }
    }, [
        assignableUsers,
        assignees,
        canConfirm,
        confirmationScopeKey,
        contactEmail,
        contactPhone,
        dentallySubmit,
        existingLead,
        normalizedReason,
        paymentValues,
        practiceId,
        reason,
        resolvedDob,
        resolvedSalutation,
        selectedSlot,
        selectedDentallyTreatment,
        hasValidReason,
    ]);

    useImperativeHandle(ref, () => ({
        submit() {
            if (!canConfirm) {
                if (!hasValidReason) setReasonTouched(true);
                return null;
            }
            handleConfirm();
            return null; // Don't advance wizard — success screen replaces content
        },
    }), [canConfirm, handleConfirm, hasValidReason]);

    // ── Success view ─────────────────────────────────────────────────────────
    if (dentallyResult?.success) {
        return (
            <DentallyBookingSuccess
                result={dentallyResult}
                practiceId={practiceId}
                bookingDiagnostic={bookingDiagnostic}
                onClose={() => onClose(true)}
            />
        );
    }

    // ── Progress / loading view ──────────────────────────────────────────────
    if (dentallyLoading) {
        const currentIdx = bookingStep?.index || 0;
        const renderStepIcon = (index) => {
            if (currentIdx > index) return <Iconify icon='mdi:check-circle' width={18} sx={{ color: 'success.main', flexShrink: 0 }} />;
            if (currentIdx === index) return <CircularProgress size={16} thickness={5} sx={{ flexShrink: 0, color: 'success.main' }} />;
            return <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: 'divider', flexShrink: 0 }} />;
        };
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={44} sx={{ mb: 2, color: 'success.main' }} />
                <Typography variant='h6' fontWeight={700} sx={{ mb: 0.5 }}>
                    Booking in progress…
                </Typography>
                {bookingStep?.label && (
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                        {bookingStep.label}
                    </Typography>
                )}
                <Box sx={{ textAlign: 'left', maxWidth: 260, mx: 'auto' }}>
                    {BOOKING_PROGRESS_STEPS.map(({ index, label }) => (
                        <Stack key={index} direction='row' alignItems='center' spacing={1} sx={{ mb: 0.75 }}>
                            {renderStepIcon(index)}
                            <Typography variant='body2' sx={{
                                color: currentIdx >= index ? 'text.primary' : 'text.disabled',
                                fontWeight: currentIdx === index ? 600 : 400,
                            }}>
                                {label}
                            </Typography>
                        </Stack>
                    ))}
                </Box>
            </Box>
        );
    }

    // ── Slot summary ────────────────────────────────────────────────────────
    const slotLabel =
        selectedSlot && !selectedSlot._manual
            ? (parseDentallyDate(selectedSlot.start_time) || new Date(selectedSlot.start_time)).toLocaleString('en-GB', {
                  weekday: 'short',
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
              })
            : null;

    // ── Normal booking form ──────────────────────────────────────────────────
    const patientDisplayName = [
        resolvedSalutation,
        existingLead?.first_name,
        existingLead?.last_name,
    ].filter(Boolean).join(' ');

    return (
        <Box>
            {/* Read-only patient summary */}
            {existingLead && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    p: 1.5, mb: 2, borderRadius: 2,
                    bgcolor: 'rgba(7,141,238,0.04)',
                    border: '1px solid rgba(7,141,238,0.16)',
                }}>
                    <Iconify icon="mdi:account-circle" width={22} sx={{ color: '#078DEE', flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                            {patientDisplayName || [existingLead.first_name, existingLead.last_name].filter(Boolean).join(' ')}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                            {resolvedDob && (
                                <Typography variant="caption" color="text.secondary">DOB: {resolvedDob}</Typography>
                            )}
                        </Stack>
                    </Box>
                </Box>
            )}

            {/* Slot summary */}
            {slotLabel && (
                <Box
                    sx={{
                        p: 1.5,
                        bgcolor: 'rgba(0,171,85,0.08)',
                        borderRadius: 2.5,
                        mb: 2,
                        border: '1px solid rgba(0,171,85,0.3)',
                    }}
                >
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Iconify icon='mdi:calendar-check' width={18} sx={{ color: 'success.main' }} />
                        <Box>
                            <Typography variant='body2' fontWeight={600}>
                                {slotLabel}
                            </Typography>
                            {selectedSlot._queried_practitioner_name && (
                                <Typography variant='caption' color='text.secondary'>
                                    {selectedSlot._queried_practitioner_name}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </Box>
            )}

            {/* The appointment type and team were selected before the patient step. */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>Booking summary</Typography>
                <Typography variant='body2' fontWeight={600}>
                    {selectionMatchesPractice ? selectedDentallyTreatment?.name : 'Choose an appointment type'}
                </Typography>
                <Typography variant='body2' color='text.secondary'>{duration} minutes</Typography>
                {!hasValidReason && (
                    <Alert severity='warning' sx={{ mt: 1 }}>
                        {showReasonError ? 'Appointment reason is required. ' : ''}
                        {reasonsLoading ? 'Checking appointment type…' : (reasonsError || 'Go back and choose an appointment type with a current Dentally reason configured in Integrations.')}
                    </Alert>
                )}
            </Box>

            {/* Payment card */}
            <Box sx={sectionCardStyle}>
                <Typography sx={sectionLabelStyle}>
                    <Iconify icon='mdi:credit-card-outline' width={15} sx={{ color: '#FFAB00' }} />
                    Payment
                </Typography>

                {/* Payment mode toggle */}
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    mb: 2, px: 1.5, py: 1, borderRadius: 1.5,
                    bgcolor: isPaymentRequired ? 'rgba(255,171,0,0.08)' : 'rgba(0,171,85,0.06)',
                    border: '1px solid',
                    borderColor: isPaymentRequired ? 'rgba(255,171,0,0.24)' : 'rgba(0,171,85,0.16)',
                }}>
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <Iconify
                            icon={isPaymentRequired ? 'mdi:cash-lock' : 'mdi:calendar-check'}
                            width={18}
                            sx={{ color: isPaymentRequired ? '#FFAB00' : '#00AB55' }}
                        />
                        <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
                            {isPaymentRequired ? 'Payment required before booking' : 'Book first — payment optional'}
                        </Typography>
                    </Stack>
                    <FormControlLabel
                        control={
                            <Switch
                                size='small'
                                checked={isPaymentRequired}
                                onChange={(e) => setExportTriggerOverride(e.target.checked ? 'PAYMENT' : 'APPOINTMENT')}
                            />
                        }
                        label={<Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Require payment</Typography>}
                        sx={{ m: 0 }}
                    />
                </Box>

                {/* Payment fields */}
                <DentallyBookingFormPayment
                    values={paymentValues}
                    onChange={(field, val) => setPaymentValues((prev) => ({ ...prev, [field]: val }))}
                    errors={{}}
                />

                {isPaymentRequired && paymentValues.paymentOption === 'none' && (
                    <Alert severity='warning' sx={{ mb: 0 }}>
                        Payment required before appointment is confirmed.
                    </Alert>
                )}
            </Box>

            {/* Contact details card — visible when sending payment link */}
            {paymentValues.paymentOption === 'link' && (
                <Box sx={sectionCardStyle}>
                    <Typography sx={sectionLabelStyle}>
                        <Iconify icon='mdi:account-circle-outline' width={15} sx={{ color: '#078DEE' }} />
                        Contact Details
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
                        Payment link will be sent to the email below. Verify details are correct.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <TextField
                            size='small'
                            label='Email'
                            type='email'
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Iconify icon='mdi:email-outline' width={18} sx={{ color: 'text.disabled', mr: 1 }} />
                                ),
                            }}
                        />
                        <TextField
                            size='small'
                            label='Phone'
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Iconify icon='mdi:phone-outline' width={18} sx={{ color: 'text.disabled', mr: 1 }} />
                                ),
                            }}
                        />
                    </Box>
                    {!contactEmail.trim() && (
                        <Alert severity='warning' sx={{ mt: 1.5, mb: 0 }}>
                            Email is required to send a payment link.
                        </Alert>
                    )}
                </Box>
            )}

            {/* The chosen time sits inside a block the practice reserved for
                something else. A statement, not a block: Dentally does not
                enforce zones either (their own docs call them "highlighting
                time in the appointment book"), so the operator decides. Said
                out loud here rather than left to the hover on the previous
                step, because this is the moment the mistake becomes real. */}
            {zoneWarning && !dentallyResult?.success && (
                <Alert severity='warning' sx={{ mb: 2 }} data-testid='dentally-zone-warning'>
                    {zoneWarning}
                </Alert>
            )}

            {/* Why the Confirm button is greyed out. Shown last, next to where the
                footer button sits, and only while nothing worse is on screen —
                a real failure is more useful than a precondition. */}
            {blockingReason && !confirmError && !dentallyError && (
                <Alert severity='info' sx={{ mb: 2 }} data-testid='dentally-confirm-blocked'>
                    {blockingReason}
                </Alert>
            )}

            {confirmError && <Alert severity='warning' sx={{ mb: 2 }}>{confirmError}</Alert>}
            {dentallyError && <Alert severity='error' sx={{ mb: 2 }}>{dentallyError.message}</Alert>}
        </Box>
    );
});

export default DentallyFollowUpConfirmWizardStep;
